import React from 'react';
import PropTypes from 'prop-types';
import { exposeViaAjax } from '@eb/janus';
import isEmpty from 'lodash/isEmpty';

/**
 * This component is used to define variants inside the Experiment component.
 */
export const Variant = ({ experimentId, id, children }) =>
    typeof children === 'function' && children(id, experimentId);

Variant.propTypes = {
    /** The variant id, usually returned by the janus exposure API*/
    id: PropTypes.string.isRequired,
    /** This component can only receive a render prop child, this ensures no code is run before the variant is selected */
    children: PropTypes.func.isRequired,
};

let exposureAPICache = {};
let exposureListeners = {};

/**
 * Use this function to centrally listen to when an experiment variant has been exposed.
 * Listeners will only be triggered once per experiment id, and only after the initial resolution.
 * Cached exposure data will not trigger these events.
 * Useful to trigger exposure heckpoints.
 *
 * @param {String} experimentId the experiment to check the exposure on.
 * @param {Function} fn (exposureData:{variant}) A function that receives the exposure data.
 */
export const setExposureListener = (experimentId, fn) => {
    // Add listener
    exposureListeners[experimentId] = fn;
};

const triggerExposureListener = (experimentId, exposureData) => {
    if (typeof exposureListeners[experimentId] == 'function') {
        exposureListeners[experimentId].call(null, exposureData);
    }
};

/**
 * FOR UNIT TESTS ONLY.
 *
 * Use this function to reset the exposure listeners.
 * Useful to ensure no dangling listeners affect your test.
 */
export const __clearExposureListenersForTest = () => {
    exposureListeners = {};
};

/**
 * This function call the janus exposure API that:
 * - Enrolls the current user on the experiment and assigns a variant.
 * - Returns the assigned variant for the current user if already enrolled.
 * This function will cache calls to the API for each experiment to avoid
 * multiple api calls.
 *
 * The exposure api response has the following shape on success:
 * ```js
 * {
 *   data: {
 *     variant: 'A',
 *   },
 *   success: true,
 * }
 * ```
 *
 * and on error:
 * ```js
 * {
 *   errors: {
 *     __all__: ['Error1', 'Error2']
 *   },
 *   success: false,
 * }
 * ```
 *
 * @param {String} experimentId the experiment to enroll the user to.
 * @param {String} domain the domain where the janus api is hosted.
 * @returns {Object} the janus exposure api response.
 */
const getExperimentExposure = async (experimentId, domain = '') => {
    // Initialize cache entry
    exposureAPICache[experimentId] = exposureAPICache[experimentId] || {};

    // Get cached data before making any api call
    let { request, data: cachedData } = exposureAPICache[experimentId];

    // If we have a cached RESPONSE, then just return that.
    if (cachedData) {
        return { data: cachedData };
    }

    // Do not trigger exposure listener by default
    let shouldTriggerExposureListener = false;

    // If no cached RESPONSE, then check if the API was already called
    // and is still resolving, by looking for a cached REQUEST
    if (!request) {
        // If not cached REQUEST, then call the API
        request = exposeViaAjax(experimentId, domain);
        // Cache the request in case getExperimentExposure gets called again
        // before the API responds
        exposureAPICache[experimentId].request = request;

        // Trigger exposure listeners if non-cached exposure
        shouldTriggerExposureListener = true;
    }

    // Wait for a response, new or cached.
    const response = await request;

    let data;
    let errors;

    if (response.status === 200) {
        // Before we try to re-parse the response we have to check the cache again.
        // In the case of multiple parallel calls to this function, when one of the calls
        // resolves, it consumes the response body and that makes the response object
        // 'disturbed', which prevents cloning. If the response was already consumed by a
        // parallel call to this function then that call has cached the result,
        // read it from there and avoid cloning a consumed response that triggers this error:
        // https://bugs.webkit.org/show_bug.cgi?id=171552
        if (response.bodyUsed && exposureAPICache[experimentId].data) {
            return {
                data: exposureAPICache[experimentId].data,
            };
        }

        // Parse to JSON, if cached we have to clone it because it is likely
        // a locked stream.
        const responseClone = response.clone();
        const responseJSON = await responseClone.json();

        // Get the data and errors if any.
        data = responseJSON.data;
        errors = responseJSON.errors;
    } else {
        errors = {
            __all__: [`Janus API returned error code ${response.status}.`],
        };
    }

    // If api response is succesfull then cache result
    if (!errors || Object.keys(errors).length === 0) {
        // Allow for request object to be garbage collected
        exposureAPICache[experimentId].request = null;
        // Cache response data
        exposureAPICache[experimentId].data = data;
    } else {
        // DO NOT trigger exposure listeners if an error occured
        shouldTriggerExposureListener = false;
    }

    if (shouldTriggerExposureListener) {
        triggerExposureListener(experimentId, data);
    }

    // return api data
    return { data, errors };
};

/**
 * FOR UNIT TESTS ONLY.
 *
 * Use this function to reset the exposure api cache.
 * Useful to force the Experiment component to request the janus API.
 */
export const __clearExposureRequestCacheForTest = () => {
    exposureAPICache = {};
};

const selectVariant = (selectedVariant, variants) =>
    [...variants]
        .filter((variant) => variant.props.id === selectedVariant)
        .shift();

const forcedExposure = {};

/**
 * FOR UNIT TESTS ONLY.
 *
 * Use this function to force a variant for an experiment across your components.
 * Usually called on `forceVariants.js` so it will automatically apply to all tests.
 * This is useful for two reasons:
 * - Avoid calling the exposure API on unit tests, so you don't need to mock it.
 * - Ensure your components have a consistent behavior on all tests.
 * @param {String} experimentId the experiment to force a variant on
 * @param {String} variant the variant id to force
 */
export const __forceExperimentVariantForTest = (experimentId, variant) => {
    forcedExposure[experimentId] = { variant };
};

const checkFVParams = () => {
    if (!window) {
        return;
    }

    const params = /janus_fv=(\S+)=(\S+)/.exec(window.location.search);

    if (params) {
        const [experimentId, variantId] = params.slice(1);

        __forceExperimentVariantForTest(experimentId, variantId);
    }
};

/**
 * Experiment component:
 *
 * This component is used to instrument A/B/N experiments.
 * You can define an experiment declaratively right into your visual components
 * and all the experiment exposure logic will be run in the background.
 * ```js
 * function MyComponent () {
 *   const renderVariantA = () => <p>Control Variant</p>
 *   const renderVariantB = () => <p>Variant B</p>
 *
 *   return (
 *      <div>
 *        Current Variant:
 *        <Experiment
 *              id="myExperiment"
 *              domain="janus-api-domain.com"
 *              controlVariantId="A"
 *        >
 *          <Variant id="A">{renderVariantA}</Variant>
 *          <Variant id="B">{renderVariantB}</Variant>
 *        </Experiment>
 *      </div>
 *   )
 * }
 * ```
 * You can instantiate as many Experiment components as you need on your code
 * and this logic will ensure the api is appropately called once per experiment.
 * You can also pass error handling variants and placeholder variants while the
 * exposure data is being loaded.
 */
export class Experiment extends React.Component {
    static propTypes = {
        /** The janus experiment name */
        id: PropTypes.string.isRequired,
        /** The domain where the janus api is hosted */
        domain: PropTypes.string.isRequired,
        /** The variants for this experiment, can only be of type Variant*/
        children: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.arrayOf(PropTypes.element),
        ]),
        /** The variant id to render as default if not participating on the event, or if an error happens */
        controlVariantId: PropTypes.string.isRequired,
        /** A render function to be called when an error occurs. Overrides control variant.*/
        errorFallback: PropTypes.func,
        /** A render function to be called while the API is processing the expuse data. */
        loadingFallback: PropTypes.func,
        /** A flag to enable ?janus_fv url parameters */
        enableFVParams: PropTypes.bool,
    };

    state = {
        exposure: null,
    };

    async componentDidMount() {
        const { id, domain, enableFVParams } = this.props;

        if (enableFVParams) {
            checkFVParams();
        }

        if (forcedExposure[id]) {
            // If forced exposure cancel API call
            this.setState({
                errors: null,
                exposure: forcedExposure[id],
            });

            return;
        }

        try {
            const { data, errors } = await getExperimentExposure(id, domain);

            // Some components that are frequently re-rendered might un-mount
            // before we get exposure data, in that case cancel updates
            // to avoid react warnings and speed up render updates
            if (this.__cancelExposureUpdate) {
                return;
            }

            if (!isEmpty(errors)) {
                this.setState({
                    errors,
                    exposure: null,
                });
            } else {
                this.setState({
                    errors: null,
                    exposure: data,
                });
            }
        } catch (error) {
            this.setState({
                errors: [error],
                exposure: null,
            });
            throw error;
        }
    }

    componentWillUnmount() {
        this.__cancelExposureUpdate = true;
    }

    render() {
        let {
            id: experimentId,
            children: variants,
            controlVariantId,
            loadingFallback = () => null,
            errorFallback,
        } = this.props;

        const { exposure, errors } = this.state;

        if (!Array.isArray(variants) || variants.length < 2) {
            throw new Error(
                'You must provide at least 2 variants for an experiment.',
            );
        }

        if (!controlVariantId) {
            throw new Error('You must specify a controlVariantId.');
        }

        const selectControlVariant = () => {
            const controlVariant = selectVariant(controlVariantId, variants);

            if (!controlVariant) {
                throw new Error(
                    'The controlVariantId defined does not match any variant.',
                );
            }

            return React.cloneElement(controlVariant, { experimentId });
        };

        errorFallback = errorFallback || selectControlVariant;

        if (!exposure) {
            if (errors) {
                return errorFallback(errors);
            }
            return loadingFallback();
        }

        let selectedVariant;

        if (exposure.variant) {
            selectedVariant = selectVariant(exposure.variant, variants);
        } else {
            return selectControlVariant();
        }

        if (!selectedVariant) {
            return errorFallback([
                new Error('Selected variant is not declared on the experiment'),
            ]);
        }

        return React.cloneElement(selectedVariant, { experimentId });
    }
}
